/**
 * Common dropdown (select) for forms and filters.
 * Use instead of raw FormControl + InputLabel + Select + MenuItem for consistent styling.
 *
 * Options can be:
 *   - { value, label } e.g. { value: 'x', label: 'Display text' }
 *   - { id, name } (e.g. from API) – use getOptionValue and getOptionLabel
 *
 * @example
 *   <CustomDropdown
 *     label="Status"
 *     value={status}
 *     onChange={(e) => setStatus(e.target.value)}
 *     options={[{ value: '', label: 'All' }, ...statusOptions]}
 *   />
 *   // With API shape (id/name):
 *   <CustomDropdown
 *     label="Status"
 *     value={statusId}
 *     onChange={(e) => setStatusId(e.target.value)}
 *     options={statusOptions}
 *     getOptionValue={(opt) => opt.id}
 *     getOptionLabel={(opt) => opt.name}
 *     emptyLabel="All"
 *   />
 */
import React from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const defaultGetValue = (opt) => {
    if (opt == null) return undefined;
    if (opt?.value !== undefined) return opt.value;
    if (opt?.id !== undefined) return opt.id;
    return undefined;
};
const defaultGetLabel = (opt) => {
    if (opt == null) return '';
    if (opt?.label !== undefined) return opt.label;
    if (opt?.name !== undefined) return opt.name;
    return String(opt?.value ?? opt?.id ?? '');
};

const CustomDropdown = ({
    label,
    value,
    onChange,
    options = [],
    getOptionValue = defaultGetValue,
    getOptionLabel = defaultGetLabel,
    emptyLabel = '',
    fullWidth = true,
    size = 'medium', // 'medium' = 56px to match CustomTextField; use size="small" for 40px in toolbars/filters
    disabled = false,
    sx = {},
    ...rest
}) => {
    // Ensure options is always an array and strip null/undefined to avoid "reading 'value' of undefined"
    const safeOptions = Array.isArray(options) ? options.filter((opt) => opt != null) : [];
    const id = label ? `custom-dropdown-${String(label).replace(/\s/g, '-')}` : undefined;
    // Normalize to string so value matches MenuItem value (onChange gives string); keeps empty as ''
    const displayValue = (value === undefined || value === null || value === '') ? '' : String(value);
    // Check if there's an option matching empty value
    const hasEmptyOption = safeOptions.some((o) => {
        const ov = getOptionValue(o);
        return ov === '' || ov == null;
    });

    const displayLabel = (v) => {
        // First check if there's a matching option (including options with empty string value)
        const opt = safeOptions.find((o) => {
            const ov = getOptionValue(o);
            // Handle empty string comparison explicitly
            if ((v === '' || v == null) && (ov === '' || ov == null)) return true;
            return ov === v || ov == v || String(ov) === String(v);
        });
        if (opt) return getOptionLabel(opt);
        // Fall back to emptyLabel for empty values
        if (v === '' || v == null) return emptyLabel || '';
        return String(v);
    };

    // Label should shrink if there's a displayable value
    const shouldShrink = !!emptyLabel || hasEmptyOption || displayValue !== '';

    return (
        <FormControl
            fullWidth={fullWidth}
            size={size}
            disabled={disabled}
            sx={{
                // Label positioning for 56px height input
                '& .MuiInputLabel-root': {
                    transform: size === 'small' ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 16px) scale(1)',
                    color: 'text.secondary',
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'white',
                    },
                    '&.Mui-focused': {
                        color: 'primary.main',
                    },
                },
                '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    backgroundColor: 'background.paper',
                    height: size === 'small' ? '40px' : '56px',
                    minHeight: size === 'small' ? '40px' : '56px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                    },
                },
                '& .MuiSelect-select': {
                    boxSizing: 'border-box',
                    paddingTop: size === 'small' ? '8px' : '16px',
                    paddingBottom: size === 'small' ? '8px' : '16px',
                    paddingLeft: '16px',
                    paddingRight: '32px',
                    color: 'text.primary',
                },
                ...sx,
            }}
            {...rest}
        >
            {label && <InputLabel id={id} shrink={shouldShrink}>{label}</InputLabel>}
            <Select
                labelId={id}
                variant="outlined"
                value={displayValue}
                onChange={onChange}
                label={label}
                displayEmpty={!!emptyLabel || hasEmptyOption}
                renderValue={displayLabel}
                MenuProps={{
                    PaperProps: { elevation: 8 },
                    disableScrollLock: false,
                }}
            >
                {emptyLabel && (
                    <MenuItem value="">
                        <em>{emptyLabel}</em>
                    </MenuItem>
                )}
                {safeOptions.map((opt, idx) => {
                    if (opt == null) return null;
                    const val = getOptionValue(opt);
                    if (val === undefined) return null;
                    // Use string so it matches displayValue (onChange returns string); ensures selected option is visible
                    const menuValue = (val === null || val === '') ? val : String(val);
                    return (
                        <MenuItem key={val !== null && val !== '' ? val : `opt-${idx}`} value={menuValue}>
                            {getOptionLabel(opt)}
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};

export default CustomDropdown;
